<template>
  <div class="samples-filter mb-5">
    <card
      title="Filtros"
      class="mb-5"
    >
      <template #headerAction>
        <div class="filter-header-pending-samples">
          <checkbox
            v-model="filter.onlyPending"
            label="Somente pendentes"
          />
        </div>
      </template>

      <div class="mb-5">
        <md-layout class="gap-4">
          <search-field-selector
            class="search-date-select-options"
            :selected-option="selectedDateOption"
            :text-value="dateQuery"
            :date-range-value="dateSelected"
            :default-start-date="defaultStartDate"
            :default-end-date="defaultEndDate"
            :clean-date="cleanDate"
            :limit-date="limitDate"
            :options="dateSelectOptions"
            :default-option-index="defaultOptionIndexDate"
            @updateSearchValue="setDate"
          />

          <search-field-selector
            class="search-multiple-text"
            :default-option-index="defaultOptionIndexText"
            :selected-option="selectedTextOption"
            :text-value="textQuery"
            :options="selectOptions"
            @update:textValue="textQuery = $event"
            @updateSearchValue="setMultipleTextSearch"
          />

          <md-button
            class="btn btn-nonenary"
            @click="submitSearch"
          >
            <svg-loader src="/static/images/icon-generate-list.svg" />

            Listar
          </md-button>
        </md-layout>
      </div>

      <md-layout class="gap-2 justify-between mb-2">
        <batch-packaging-select v-model="packagingSelected" />

        <div>
          <md-layout class="gap-4">
            <md-radio
              v-for="option in radioOptions"
              :key="option.value"
              v-model="printedRoot"
              :md-value="option.value"
              class="radio"
            >
              {{ option.label }}
            </md-radio>
          </md-layout>
        </div>
      </md-layout>

      <div
        class="more-filters"
        :class="{ 'more-filters-open': showMoreFilters }"
      >
        <md-button
          class="more-filters-button"
          @click="toggleMoreFilters"
        >
          Mais filtros

          <md-icon
            md-src="/static/images/arrow-up-primary-color.svg"
            class="more-filters-button-icon"
          />
        </md-button>

        <transition name="dropdown">
          <div v-show="showMoreFilters">
            <div>
              <p class="mb-2">
                Origem da OS:
              </p>

              <md-layout class="gap-4">
                <md-radio
                  v-for="(option, index) in osOriginOptions"
                  :key="index"
                  v-model="filter.source"
                  :md-value="option.value"
                  class="mb-0"
                >
                  {{ option.label }}
                </md-radio>
              </md-layout>
            </div>

            <md-layout class="gap-2 align-end mt-4">
              <div>
                <p class="mb-2">
                  Selecione por tipo de cadastro (Apenas AOL):
                </p>

                <md-layout class="gap-4">
                  <md-radio
                    v-for="option in typeRegisterOptions"
                    :key="option.value"
                    v-model="integratedRoot"
                    :md-value="option.value"
                    class="mb-0"
                    :disabled="filter.source === 'AOLWS'"
                    @input="onGetFiles"
                  >
                    {{ option.label }}
                  </md-radio>
                </md-layout>
              </div>

              <md-layout v-if="integratedRoot">
                <field-container label="Arquivos (XML)">
                  <div>
                    <div v-if="filesSentStatus === 'STATUS_END'">
                      <model-list-select
                        id="comboFiles"
                        v-model="file"
                        v-validate="'required'"
                        option-text="name"
                        placeholder="Selecione um arquivo"
                        :options="getAvailableFiles()"
                        option-value="id_file"
                        :disabled="!dateSelected"
                      >
                        <span slot="no-options">
                          Não foram encontrados arquivos com OS's transferidas para o período selecionado.
                        </span>
                      </model-list-select>
                    </div>

                    <div v-else>
                      <spinner class="small search__loading" />
                    </div>
                  </div>
                </field-container>
              </md-layout>
            </md-layout>
          </div>
        </transition>
      </div>
    </card>

    <div class="samples-filter-header">
      <label-data-title
        label="Listagem de amostras"
        :data="currentFilterLabel"
      />

      <div class="samples-filter-header-buttons">
        <button
          class="btn btn-primary"
          :disabled="!isPrintReceiptEnabled"
          @click="loadingSamplesReceipt"
        >
          <svg-loader
            class="icon-print"
            src="/static/images/icon-print-alternative-white.svg"
          />

          IMPRIMIR RECIBO

          <span v-if="selectedSamples.length > 0">({{ selectedSamples.length }})</span>
        </button>

        <button
          v-if="webSocketStatus.connectionState === 'connected'"
          class="btn btn-primary"
          :disabled="!hasSamples"
          @click="handlePrintSamples"
        >
          <svg-loader
            class="icon-print"
            src="/static/images/icon-print-alternative-white.svg"
          />

          IMPRIMIR ETIQUETAS

          <span v-if="selectedSamples.length > 0">({{ selectedSamples.length }})</span>
        </button>
      </div>
    </div>

    <modal
      ref="loadingReceiptModal"
      template="loading"
      title="Aguarde"
      ref-name="loadingReceiptModal"
    >
      <p>Gerando recibo...</p>
    </modal>

    <PrinterModal ref="printerModal" />

    <sample-modal-confirmation
      ref="samplesQuestionModal"
      @printTags="print"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import moment from 'moment'

import { formatDate } from '@/utils/date-utils'

import {
  BATCH_PACKAGING_FROZEN_ID,
  BATCH_PACKAGING_REFRIGERATED_DRY_ID,
  BATCH_PACKAGING_REFRIGERATED_ID,
  BATCH_PACKAGING_ROOM_TEMPERATURE_ID
} from '@/enums'

import notify from '@/api/notification'

import Card from '@/components/common/Card'
import ButtonIcon from '@/components/common/ButtonIcon'
import SearchFieldSelector from '@/components/common/SearchFieldSelectorV2'
import Spinner from '@/components/common/Spinner'
import FieldContainer from '@/components/common/FieldContainer'
import SelectSearch from '@/components/common/SelectSearch'
import BatchPackagingSelect from '@/components/common/BatchPackagingSelect'
import LabelDataTitle from '@/components/common/LabelDataTitle'
import Checkbox from '@/components/common/Checkbox'
import PrinterModal from '@/components/common/printer/PrinterModal'

import SamplesPrintButtons from './SamplesPrintButtons'
import SampleModalConfirmation from './SampleModalConfirmation'

export default {
  name: 'SamplesFilter',
  components: {
    Card,
    ButtonIcon,
    SearchFieldSelector,
    Spinner,
    FieldContainer,
    SelectSearch,
    BatchPackagingSelect,
    LabelDataTitle,
    SamplesPrintButtons,
    Checkbox,
    PrinterModal,
    SampleModalConfirmation
  },
  data() {
    return {
      today: new Date(),
      cleanDate: 0,
      filter: {
        source: '',
        onlyPending: false,
      },
      activeFilter: null,
      typeDateSelected: null,
      dateSelected: null,
      limitDaysSamples: 20,
      patientNameRoot: null,
      codeOriginAlvaroRoot: null,
      codeOriginLisRoot: null,
      batchLabelRoot: null,
      packagingSelected: [
        BATCH_PACKAGING_FROZEN_ID,
        BATCH_PACKAGING_REFRIGERATED_ID,
        BATCH_PACKAGING_ROOM_TEMPERATURE_ID,
        BATCH_PACKAGING_REFRIGERATED_DRY_ID
      ],
      file: null,
      filesAvailables: [],
      integratedRoot: '',
      printedRoot: '',
      showMoreFilters: false,
      osOriginOptions: [
        {
          value: '',
          label: 'Todas'
        },
        {
          value: 'AOL2',
          label: 'AOL'
        },
        {
          value: 'AOLWS',
          label: 'LIS'
        }
      ],
      dateSelectOptions: [
        {
          id: 'transferDate',
          itemValue: 'TD',
          label: 'Data da transferência',
          placeholder: 'Data da transferência',
          limitDays: 1827,
          type: 'date'
        },
        {
          id: 'collectDate',
          itemValue: 'CD',
          label: 'Data da coleta',
          placeholder: 'Data da coleta',
          limitDays: 1827,
          type: 'date'
        }
      ],
      selectOptions: [
        {
          id: 'patientName',
          label: 'Nome do Paciente',
          placeholder: 'Nome do Paciente',
          limitDays: null,
          singleDate: false,
        },
        {
          id: 'codeOriginAlvaro',
          label: 'OS Alvaro',
          placeholder: 'Os Alvaro',
          limitDays: null,
          singleDate: false,
        },
        {
          id: 'codeOriginLis',
          label: 'OS Lis',
          placeholder: 'OS Lis',
          limitDays: null,
          singleDate: false,
        },
      ],
      radioOptions: [
        { value: '', label: 'Todas' },
        { value: true, label: 'Impressas' },
        { value: false, label: 'Não impressas' }
      ],
      typeRegisterOptions: [
        { value: '', label: 'Todos' },
        { value: false, label: 'Manual' },
        { value: true, label: 'Via importação' }
      ],
      selectedDateOption: null,
      dateQuery: '',
      selectedTextOption: null,
      textQuery: '',
      defaultOptionIndex: 0,
      defaultOptionIndexDate: 0,
      defaultOptionIndexText: 0,
      processingOrigin: null,
      idRecollect: null,
      isRecollectRedirect: false,
      showOnlyPendingSamples: false,
    }
  },
  computed: {
    ...mapGetters([
      'filesUploaded',
      'filesSentStatus',
      'profile',
      'samples',
      'webSocketStatus',
      'selectedSamples',
      'toggle',
      'defaultPrinter'
    ]),
    isPrintReceiptEnabled() {
      return this.hasSamples && !this.selectedSamples.some(sample => sample.origem === 'LIS')
    },
    hasSamples() {
      return this.samples.length > 0
    },
    currentFilterLabel() {
      const formattedStartDate = moment(this.activeFilter?.dateSelected?.start ?? this.today).format('DD/MM/YYYY')
      const formattedEndDate = moment(this.activeFilter?.dateSelected?.end ?? this.today).format('DD/MM/YYYY')

      let formattedDate = formattedStartDate === formattedEndDate ? formattedStartDate : `${formattedStartDate} até ${formattedEndDate}`

      if (formattedDate === moment(this.today).format('DD/MM/YYYY')) {
        formattedDate = 'hoje'
      }

      return `de ${formattedDate} (${this.samples.length})`
    },
    defaultStartDate() {
      const query = this.$route.query
      return query.startDate ? new Date(query.startDate) : this.today
    },

    defaultEndDate() {
      const query = this.$route.query
      return query.endDate ? new Date(query.endDate) : this.today
    },

    sampleFilter() {
      const disabledRangeDate = this.idRecollect || this.codeOriginLisRoot || this.codeOriginAlvaroRoot;

      return {
        startDate: disabledRangeDate ? undefined : this.startDate,
        endDate: disabledRangeDate ? undefined : this.endDate,
        dateType: disabledRangeDate ? undefined : this.typeDateSelected,
        patientName: this.patientNameRoot ? this.patientNameRoot : undefined,
        codeOriginLis: this.codeOriginLisRoot ? this.codeOriginLisRoot : undefined,
        codeOriginAlvaro: this.codeOriginAlvaroRoot ? this.codeOriginAlvaroRoot : undefined,
        packagingType: this.packagingSelected,
        integrated: this.integratedRoot,
        printed: this.printedRoot,
        idFile: this.showMoreFilters && this.integratedRoot ? this.file?.id_file ?? undefined : undefined,
        processingOrigin: this.processingOrigin,
        idRecollect: this.idRecollect,
        source: this.filter.source ? this.filter.source : undefined,
        sampleStatus: this.filter.onlyPending ? 1 : undefined,
      };
    },

    limitDate() {
      return moment(this.today, 'DD/MM/YYYY').subtract(this.limitDaysSamples, 'days').toDate()
    },

    startDate() {
      return formatDate(this.dateSelected?.start, 4)
    },

    endDate() {
      return formatDate(this.dateSelected?.end, 4)
    }
  },
  watch: {
    dateSelected(newValue) {
      if (newValue) {
        this.dateQuery = `${newValue.start} - ${newValue.end}`
      }
    },
    textQuery(newValue) {
      if (!newValue) {
        this.selectedTextOption = null
      }
    }
  },
  async mounted() {
    this.setInitialFilters()
    this.selectSampleList(this.sampleFilter)
    this.$nextTick(async () => {
      await this.getServiceSample()
      this.getFiles()
    })
  },
  methods: {
    ...mapActions([
      'selectSampleList',
      'getServiceSample',
      'getFilesSent',
      'printReceipt',
      'printSamples',
      'webSocketPrintSamples',
      'syncPrinterFromStorage'
    ]),
    setInitialFilters() {
      this.typeDateSelected = 'TD'
      this.dateSelected = { start: this.today, end: this.today }

      this.setFiltersFromUrl()
      this.cleanDate++
    },
    setFiltersFromUrl() {
      const query = this.$route.query;

      if (query.startDate && query.endDate) {
        const startDate = this.parseDate(query.startDate);
        const endDate = this.parseDate(query.endDate);

        this.dateSelected = {
          start: startDate,
          end: endDate,
        };
      }

      if (query.dateType) {
        this.typeDateSelected = query.dateType;
        this.defaultOptionIndexDate = query.dateType === 'CD' ? 1 : 0;
      }

      if (query.codeOriginAlvaro) {
        this.codeOriginAlvaroRoot = query.codeOriginAlvaro;
        this.textQuery = query.codeOriginAlvaro;
        this.defaultOptionIndexText = 1;
      }

      if (query.codeOriginLis) {
        this.codeOriginLisRoot = query.codeOriginLis;
        this.textQuery = query.codeOriginLis;
        this.defaultOptionIndexText = 2;
      }

      if (query.patientName) {
        this.patientNameRoot = query.patientName;
        this.textQuery = query.patientName;
        this.defaultOptionIndexText = 0;
      }

      if (query.integrated) {
        this.integratedRoot = query.integrated;
        this.showMoreFilters = true;
      }

      if (query.printed) {
        this.printedRoot = query.printed === 'true';
      }

      if (query.packagingType) {
        this.packagingSelected = query.packagingType.split(',');

        this.icons.forEach(icon => {
          icon.value = this.packagingSelected.includes(icon.id);
        });
      }

      if (query.idFile) {
        this.file = this.getAvailableFiles().find(file => file.id_file === query.idFile);
      }

      if (query.processingOrigin) {
        this.processingOrigin = query.processingOrigin;
      }

      if (query.idRecollect) {
        this.idRecollect = query.idRecollect;
        this.isRecollectRedirect = true;
      }
    },
    setDate(e) {
      const { itemValue, date } = e;
      this.selectedDateOption = e;
      this.typeDateSelected = itemValue;

      const maxDays = 21;
      const startDate = moment(date.start);
      const endDate = moment(date.end);
      const diffDays = endDate.diff(startDate, 'days');

      if (diffDays > maxDays) {
        this.cleanDate++;
        this.dateSelected = {
          start: this.today,
          end: this.today,
        };

        notify({
          title: 'AVISO:',
          message: `Selecione um período de até ${maxDays} dias.`,
        });

        this.dateSelected = { start: this.today, end: this.today }
      } else {
        this.dateSelected = date;
      }
    },
    setMultipleTextSearch(e) {
      this.selectedTextOption = e
      this.textQuery = e?.query || ''

      this.patientNameRoot = ''
      this.codeOriginAlvaroRoot = ''
      this.codeOriginLisRoot = ''
      this.batchLabelRoot = ''

      switch (e?.id) {
        case 'patientName':
          this.patientNameRoot = e?.query
          break
        case 'codeOriginAlvaro':
          this.codeOriginAlvaroRoot = e?.query
          break
        case 'codeOriginLis':
          this.codeOriginLisRoot = e?.query
          break
      }
    },
    async submitSearch() {
      this.selectSampleList(this.sampleFilter)
      await this.getServiceSample()
      await this.getFiles()

      this.activeFilter = {
        dateSelected: this.dateSelected
      }
    },
    async getFiles() {
      const params = {
        startDate: this.startDate,
        endDate: this.endDate,
        idLaboratory: this.profile?.id_laboratory,
        stateList: 'TRANSMITTED'
      }
      await this.getFilesSent(params)
      this.filesAvailables = this.getAvailableFiles()
    },
    getAvailableFiles() {
      if (!this.filesUploaded) {
        return []
      }
      const clonedFilesArray = this.filesUploaded.slice(0)
      clonedFilesArray.map(file => {
        file.label = file.name
        file.value = file.id_file
      })
      return clonedFilesArray
    },
    toggleMoreFilters() {
      this.showMoreFilters = !this.showMoreFilters
    },
    onGetFiles() {
      if (!this.integratedRoot) return
      this.getFiles()
    },
    parseDate(dateStr) {
      return moment(dateStr, 'DD/MM/YYYY').toDate()
    },
    async loadingSamplesReceipt() {
      this.$refs.loadingReceiptModal.open()

      const samplesToPrint = this.selectedSamples?.length > 0 ? this.selectedSamples : this.samples
      const samplesToPrintIds = samplesToPrint.map(sample => sample.sample_id).filter(Boolean)

      await this.printReceipt(samplesToPrintIds)
      this.$refs.loadingReceiptModal.close()
      this.pushEventToAnalytics('Impressão de recibo de amostra')
    },
    pushEventToAnalytics(action) {
      this.$analytics.pushAnalyticsEvent('ga-event', {
        category: 'Impressão de Etiquetas - Amostras',
        action,
        'id-user': this.profile.id,
        'id-lab': this.profile.id_laboratory,
        'name-lab': this.profile.laboratoryName
      })
    },
    async handlePrintSamples() {
      await this.executeWithPrinterValidation(() => {
        const samplesToPrint = this.selectedSamples?.length > 0 ? this.selectedSamples : this.samples
        const barcodeList = [...new Set(samplesToPrint.map(sample => sample.bar_code).filter(Boolean))]
        const batchList = [...new Set(samplesToPrint.map(sample => sample.batch_identifier).filter(Boolean))]
        const payload = samplesToPrint.map(sample => ({
          sample_id: sample.sample_id,
          bar_code: sample.bar_code,
          batch: {
            label: sample.batch_label,
            id: sample.batch_id ?? sample.batch_identifier,
          },
          batchList,
          barcodeList,
        }))

        const isThereSomeBatchToClose = samplesToPrint.some(sample => sample.batch_identifier)

        if (isThereSomeBatchToClose) {
          this.$refs.samplesQuestionModal.openModalConfirmation(payload)
        } else {
          this.print('yes', payload)
        }
      })
    },
    openConfigModal() {
      this.$refs.printerModal.open();
    },
    hasValidPrinter() {
      this.syncPrinterFromStorage()

      return !!this.defaultPrinter;
    },
    async executeWithPrinterValidation(action) {
      const isWebSocketConnected = this.webSocketStatus?.connectionState === 'connected';

      if (!isWebSocketConnected) {
        this.$refs.printerPluginDownloadModal.open();
        return;
      }

      const hasValidPrinter = this.hasValidPrinter();

      if (hasValidPrinter) {
        await this.$nextTick();
        action();
      } else {
        this.openConfigModal();
      }
    },
    async print(answer, samplesToPrint) {
      try {
        if (this.toggle.printerPlugin) {
          await this.webSocketPrintSamples({
            samplesId: samplesToPrint,
            printBatch: answer === 'yes',
            batch: {},
          });

          await this.getServiceSample();
        } else {
          this.printSamples({
            samplesId: samplesToPrint,
            printBatch: answer === 'yes',
          });
        }
      } catch (error) {
        console.error('Erro na impressão:', error);
      }
    },
  }
}
</script>

<style lang="scss" scoped>
@use '@/../static/stylesheets/variables' as *;

.samples-filter {
  .more-filters {
    &-button {
      margin: 0;
      padding: 0;
      background-color: transparent;
      color: $theme-primary-color;
      min-height: auto;
      height: auto;

      &:hover {
        background-color: transparent;
      }

      &-icon {
        transform: rotate(180deg);
        transition: transform 0.3s ease-in-out;
      }

      .md-icon {
        min-width: auto;
        min-height: auto;
        width: 1rem;
        height: 1rem;
      }

      ::v-deep(.md-ink-ripple) {
        display: none !important;
      }
    }

    &.more-filters-open .more-filters-button-icon {
      transform: rotate(0deg) !important;
    }
  }

  .btn {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    font-family: 'Exo 2';
    font-size: 0.75rem;
    font-weight: 600;
    height: 40px;
    line-height: 0;
    text-transform: uppercase;
  }

  .md-radio {
    margin: 0;
  }

  &-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;

    &-buttons {
      display: flex;
      gap: 1rem;

      .btn {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.5rem 1rem;
        font-family: 'Exo 2';
        font-size: 0.75rem;
        font-weight: 600;
        height: 40px;
        line-height: 0;
        text-transform: uppercase;

        .md-icon {
          min-width: auto;
          min-height: auto;
          width: 1rem;
          height: 1rem;
        }
      }
    }
  }
}

:deep(.search .search-field-selector) {
  height: 40px;
  min-height: auto;
}

:deep(.md-theme-default.md-radio .md-radio-container:after) {
  background-color: $theme-primary-color !important;
}

:deep(.md-theme-default.md-radio.md-checked .md-radio-container) {
  border-color: $theme-primary-color !important;
}

.icon-filter {
  color: $theme-primary-color;
}

.search-date-select-options,
.search-multiple-text {
  width: auto;
  flex: 1;
  margin: 0;
}

.container-file {
  width: 40%;
}

.filter-header-pending-samples {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding-left: 2rem;
}

::v-deep(.card-form__container .title-main) {
  color: $theme-primary-color;
}
</style>
